import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Table,
  Tag,
  message,
  Space,
  Input,
  Select,
  DatePicker,
  Button,
  Modal,
  Descriptions,
  Spin,
} from 'antd';
import { SearchOutlined, EyeOutlined } from '@ant-design/icons';
import { api } from '../services/api';
import dayjs, { Dayjs } from 'dayjs';

interface DeletedRecordItem {
  entityType: string;
  entityTypeLabel: string;
  entityId: number;
  title: string;
  summary?: string | null;
  deletedAt?: string | null;
  deletedBy?: number | null;
  deletedByName?: string | null;
  hasDetail: boolean;
}

interface PaginationResponse<T> {
  data: T[];
  page: number;
  pageSize: number;
  totalCount: number;
}

interface DocumentDetail {
  entityType: string;
  entityTypeLabel: string;
  documentNo: string;
  documentDate: string;
  documentTypeName: string;
  accountName?: string;
  warehouseName?: string;
  vehiclePlate?: string;
  status: string;
  totalAmount: number;
  totalTax: number;
  currencySymbol?: string;
  notes?: string;
  deletedAt?: string;
  deletedByName?: string;
  lines: Array<{
    lineNo: number;
    productCode: string;
    productName: string;
    variantSku?: string;
    unitName: string;
    quantity: number;
    unitPrice: number;
    lineTotal: number;
  }>;
}

interface ServiceOrderDetail {
  entityType: string;
  entityTypeLabel: string;
  orderNo: string;
  orderDate: string;
  customerName: string;
  vehiclePlate: string;
  responsibleUserName?: string;
  statusText: string;
  totalAmount: number;
  notes?: string;
  complaintNotes?: string;
  deletedAt?: string;
  deletedByName?: string;
  lines: Array<{
    lineNo: number;
    itemTypeText: string;
    itemName: string;
    productCode?: string;
    quantity: number;
    unitPrice: number;
    lineTotal: number;
  }>;
}

const ENTITY_TYPE_OPTIONS = [
  { value: 'Document', label: 'Fiş' },
  { value: 'ServiceOrder', label: 'Servis Fişi' },
  { value: 'Product', label: 'Ürün' },
  { value: 'Account', label: 'Cari' },
  { value: 'User', label: 'Kullanıcı' },
  { value: 'Expense', label: 'Gider' },
  { value: 'CustomerVehicle', label: 'Müşteri Aracı' },
];

const DeletedRecords: React.FC = () => {
  const [items, setItems] = useState<DeletedRecordItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [entityType, setEntityType] = useState<string | undefined>();
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [documentDetail, setDocumentDetail] = useState<DocumentDetail | null>(null);
  const [serviceOrderDetail, setServiceOrderDetail] = useState<ServiceOrderDetail | null>(null);

  const loadItems = useCallback(async (page = pagination.current, pageSize = pagination.pageSize) => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = { page, pageSize };
      if (search.trim()) params.search = search.trim();
      if (entityType) params.entityType = entityType;
      if (fromDate) params.fromDate = fromDate.startOf('day').toISOString();
      if (toDate) params.toDate = toDate.endOf('day').toISOString();

      const { data } = await api.get<PaginationResponse<DeletedRecordItem>>('/deletedrecords', { params });
      setItems(data?.data ?? []);
      setPagination({
        current: data?.page ?? page,
        pageSize: data?.pageSize ?? pageSize,
        total: data?.totalCount ?? 0,
      });
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Silinen kayıtlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [search, entityType, fromDate, toDate, pagination.current, pagination.pageSize]);

  useEffect(() => {
    loadItems(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    setPagination((p) => ({ ...p, current: 1 }));
    loadItems(1, pagination.pageSize);
  };

  const openDetail = async (record: DeletedRecordItem) => {
    if (!record.hasDetail) {
      message.info('Bu kayıt tipi için detay görünümü yok.');
      return;
    }
    setDetailOpen(true);
    setDetailLoading(true);
    setDocumentDetail(null);
    setServiceOrderDetail(null);
    try {
      const { data } = await api.get(`/deletedrecords/${record.entityType}/${record.entityId}`);
      if (record.entityType === 'Document') {
        setDocumentDetail(data as DocumentDetail);
      } else if (record.entityType === 'ServiceOrder') {
        setServiceOrderDetail(data as ServiceOrderDetail);
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Detay yüklenemedi');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const columns = [
    {
      title: 'Tip',
      dataIndex: 'entityTypeLabel',
      key: 'entityTypeLabel',
      width: 120,
      render: (text: string, r: DeletedRecordItem) => (
        <Tag color={r.hasDetail ? 'blue' : 'default'}>{text}</Tag>
      ),
    },
    {
      title: 'Kayıt',
      key: 'title',
      render: (_: unknown, r: DeletedRecordItem) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.title || `#${r.entityId}`}</div>
          {r.summary ? (
            <div style={{ fontSize: 12, color: '#888' }}>{r.summary}</div>
          ) : null}
        </div>
      ),
    },
    {
      title: 'Silen',
      dataIndex: 'deletedByName',
      key: 'deletedByName',
      width: 160,
      render: (v?: string | null) => v || <span style={{ color: '#999' }}>—</span>,
    },
    {
      title: 'Silinme Tarihi',
      dataIndex: 'deletedAt',
      key: 'deletedAt',
      width: 170,
      render: (d?: string | null) => (d ? dayjs(d).format('DD.MM.YYYY HH:mm') : '—'),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_: unknown, r: DeletedRecordItem) =>
        r.hasDetail ? (
          <Button type="link" icon={<EyeOutlined />} onClick={() => openDetail(r)}>
            Detay
          </Button>
        ) : null,
    },
  ];

  const money = (v: number, symbol = '₺') =>
    `${symbol}${Number(v ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div>
      <Card title="Silinen Kayıtlar" extra={<span style={{ color: '#888', fontSize: 13 }}>Salt okunur arşiv</span>}>
        <Space wrap style={{ marginBottom: 16 }}>
          <Input
            placeholder="No / ad ara..."
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 200 }}
            allowClear
          />
          <Select
            placeholder="Kayıt tipi"
            allowClear
            style={{ width: 160 }}
            value={entityType}
            onChange={(v) => setEntityType(v)}
            options={ENTITY_TYPE_OPTIONS}
          />
          <DatePicker
            placeholder="Başlangıç"
            value={fromDate}
            onChange={(d) => setFromDate(d)}
            format="DD.MM.YYYY"
          />
          <DatePicker
            placeholder="Bitiş"
            value={toDate}
            onChange={(d) => setToDate(d)}
            format="DD.MM.YYYY"
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            Ara
          </Button>
        </Space>

        <Table
          rowKey={(r) => `${r.entityType}-${r.entityId}`}
          columns={columns}
          dataSource={items}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (t) => `Toplam ${t} kayıt`,
            onChange: (page, pageSize) => {
              setPagination((p) => ({ ...p, current: page, pageSize }));
              loadItems(page, pageSize);
            },
          }}
          onRow={(record) =>
            record.hasDetail
              ? {
                  onClick: () => openDetail(record),
                  style: { cursor: 'pointer' },
                }
              : {}
          }
        />
      </Card>

      <Modal
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={[
          <Button key="close" onClick={() => setDetailOpen(false)}>
            Kapat
          </Button>,
        ]}
        title={
          documentDetail
            ? `Silinen Fiş — ${documentDetail.documentNo}`
            : serviceOrderDetail
              ? `Silinen Servis Fişi — ${serviceOrderDetail.orderNo}`
              : 'Silinen Kayıt Detayı'
        }
        width={860}
        destroyOnClose
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : documentDetail ? (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Fiş Tipi">{documentDetail.documentTypeName}</Descriptions.Item>
              <Descriptions.Item label="Tarih">
                {dayjs(documentDetail.documentDate).format('DD.MM.YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Cari">{documentDetail.accountName || '—'}</Descriptions.Item>
              <Descriptions.Item label="Depo">{documentDetail.warehouseName || '—'}</Descriptions.Item>
              <Descriptions.Item label="Plaka">{documentDetail.vehiclePlate || '—'}</Descriptions.Item>
              <Descriptions.Item label="Durum">{documentDetail.status}</Descriptions.Item>
              <Descriptions.Item label="Toplam">
                {money(documentDetail.totalAmount, documentDetail.currencySymbol || '₺')}
              </Descriptions.Item>
              <Descriptions.Item label="KDV">{money(documentDetail.totalTax, documentDetail.currencySymbol || '₺')}</Descriptions.Item>
              <Descriptions.Item label="Silen">{documentDetail.deletedByName || '—'}</Descriptions.Item>
              <Descriptions.Item label="Silinme">
                {documentDetail.deletedAt ? dayjs(documentDetail.deletedAt).format('DD.MM.YYYY HH:mm') : '—'}
              </Descriptions.Item>
              {documentDetail.notes ? (
                <Descriptions.Item label="Not" span={2}>
                  {documentDetail.notes}
                </Descriptions.Item>
              ) : null}
            </Descriptions>
            <Table
              size="small"
              pagination={false}
              rowKey={(r) => String(r.lineNo)}
              dataSource={documentDetail.lines}
              columns={[
                { title: '#', dataIndex: 'lineNo', width: 50 },
                { title: 'Kod', dataIndex: 'productCode', width: 110 },
                { title: 'Ürün', dataIndex: 'productName' },
                { title: 'Birim', dataIndex: 'unitName', width: 80 },
                { title: 'Miktar', dataIndex: 'quantity', width: 80 },
                {
                  title: 'Birim Fiyat',
                  dataIndex: 'unitPrice',
                  width: 110,
                  render: (v: number) => money(v, documentDetail.currencySymbol || '₺'),
                },
                {
                  title: 'Satır Toplam',
                  dataIndex: 'lineTotal',
                  width: 120,
                  render: (v: number) => money(v, documentDetail.currencySymbol || '₺'),
                },
              ]}
            />
          </>
        ) : serviceOrderDetail ? (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Müşteri">{serviceOrderDetail.customerName || '—'}</Descriptions.Item>
              <Descriptions.Item label="Plaka">{serviceOrderDetail.vehiclePlate || '—'}</Descriptions.Item>
              <Descriptions.Item label="Fiş Tarihi">
                {dayjs(serviceOrderDetail.orderDate).format('DD.MM.YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Durum">{serviceOrderDetail.statusText}</Descriptions.Item>
              <Descriptions.Item label="Sorumlu">{serviceOrderDetail.responsibleUserName || '—'}</Descriptions.Item>
              <Descriptions.Item label="Toplam">{money(serviceOrderDetail.totalAmount)}</Descriptions.Item>
              <Descriptions.Item label="Silen">{serviceOrderDetail.deletedByName || '—'}</Descriptions.Item>
              <Descriptions.Item label="Silinme">
                {serviceOrderDetail.deletedAt
                  ? dayjs(serviceOrderDetail.deletedAt).format('DD.MM.YYYY HH:mm')
                  : '—'}
              </Descriptions.Item>
              {serviceOrderDetail.notes ? (
                <Descriptions.Item label="Not" span={2}>
                  {serviceOrderDetail.notes}
                </Descriptions.Item>
              ) : null}
              {serviceOrderDetail.complaintNotes ? (
                <Descriptions.Item label="Şikayet" span={2}>
                  {serviceOrderDetail.complaintNotes}
                </Descriptions.Item>
              ) : null}
            </Descriptions>
            <Table
              size="small"
              pagination={false}
              rowKey={(r) => String(r.lineNo)}
              dataSource={serviceOrderDetail.lines}
              columns={[
                { title: '#', dataIndex: 'lineNo', width: 50 },
                { title: 'Tip', dataIndex: 'itemTypeText', width: 100 },
                { title: 'Kalem', dataIndex: 'itemName' },
                { title: 'Kod', dataIndex: 'productCode', width: 100 },
                { title: 'Miktar', dataIndex: 'quantity', width: 80 },
                {
                  title: 'Birim Fiyat',
                  dataIndex: 'unitPrice',
                  width: 110,
                  render: (v: number) => money(v),
                },
                {
                  title: 'Satır Toplam',
                  dataIndex: 'lineTotal',
                  width: 120,
                  render: (v: number) => money(v),
                },
              ]}
            />
          </>
        ) : null}
      </Modal>
    </div>
  );
};

export default DeletedRecords;
